import { UiAtlas, UiIcon } from "./UiIcon";
import { useEffect, useRef, useState } from "react";
import { useBackToClose } from "./useDialogA11y";
import {
  HERO_BASE_PRESETS,
  heroBaseKey,
  stringifyParts,
  sanitizeNickname,
  type HeroBase,
} from "@greg-chick/shared";
import {
  pickableOptions,
  pickableBases,
  visibleSteps,
  isCustomMode,
  baseLabelOf,
  type PickCategory,
} from "../lib/heroOptions";
import { useHeroStore } from "../store/heroStore";
import { HeroAvatar } from "./HeroAvatar";
import { HeroCard } from "./HeroCard";
import { track } from "../lib/ga4";
import { loadAssetManifest, type AssetManifest } from "../lib/assetManifest";
import { heroSavedOk } from "../lib/heroStorage";

// 기본 제공 캐릭터 이름은 shared(HERO_BASE_PRESETS) 단일 출처 — 관리자와 같은 이름을 쓴다.
const BASE_LABEL: Record<string, string> = {
  ...Object.fromEntries(HERO_BASE_PRESETS.map((p) => [p.id, p.label])),
  custom: "🎨 직접 꾸미기",
};
// 완성형 프리셋 캐릭터 목록은 **그림이 있는 것만**(pickableBases) — 운영자용 빈 자리
// (explorer_f~i)가 빈 칸으로 아이 화면에 나오면 안 된다. 모자(c5~c8)와 같은 규칙.
// 직접 꾸미기 = 몸(아이) × 옷(룩). 각 조합은 "그 아이가 그 옷을 입은" 통짜 이미지(hero.look.<몸>_<옷>) — 자연 착장.
// 표정은 룩에 포함(별도 오버레이 안 함 → 얹은 느낌 방지). 모자·소품만 따로 얹는다(각자 제자리로 그려진 그림).
// 선택은 단계형(1 아이 → 2 옷 → 3 모자 → 4 소품).
const OPT_LABEL: Record<string, string> = {
  b1: "아이 1",
  b2: "아이 2",
  b3: "아이 3",
  b4: "아이 4",
  o0: "기본",
  o1: "겨울",
  o2: "탐험가",
  o3: "우비",
  c0: "안 써요",
  c1: "탐험가 모자",
  c2: "털모자",
  c3: "리본핀",
  c4: "리본",
  a0: "안 들어요",
  a1: "가방",
  a2: "별",
  a3: "랜턴",
  a4: "완주 메달",
};

// 단계 정의 — 라벨과 안내문을 한 곳에서. 3·4단계는 게시된 그림이 있을 때만 노출된다.
type StepId = 1 | 2 | 3 | 4;
const STEPS: { id: StepId; cat: PickCategory; icon: string; name: string; guide: string }[] = [
  { id: 1, cat: "body", icon: "①", name: "아이", guide: "누구랑 모험을 떠날까요?" },
  { id: 2, cat: "outfit", icon: "②", name: "옷", guide: "어떤 옷을 입혀볼까요?" },
  { id: 3, cat: "hair", icon: "③", name: "모자", guide: "머리에는 무얼 쓸까요?" },
  { id: 4, cat: "acc", icon: "④", name: "소품", guide: "무얼 들고 갈까요?" },
];

interface Props {
  onSaved: () => void;
  onClose: () => void;
}

/** 히어로 커스터마이저(W-01). 파츠 조합 + idle 미리보기 + 닉네임 + 저장. */
function HeroCustomizerInner({ onSaved, onClose }: Props) {
  /**
   * 📱 **뒤로 가기로도 닫는다.**
   *
   * 처음엔 「뒤로가 취소가 되어 꾸미던 것이 사라진다」고 걱정해 일부러 뺐다. 그런데 재 보니
   * **꾸미던 것은 닫아도 남는다**(스토어 draft — ✕ 로 닫고 다시 열면 그대로다).
   * 그러니 뒤로로 닫아도 잃을 것이 없고, 지금처럼 **화면을 통째로 나가는 것보다 낫다.**
   */
  useBackToClose(onClose);
  const draft = useHeroStore((s) => s.draft);
  const parts = useHeroStore((s) => s.parts)();
  const setBase = useHeroStore((s) => s.setBase);
  const setPart = useHeroStore((s) => s.setPart);
  const randomize = useHeroStore((s) => s.randomize);
  const setNickname = useHeroStore((s) => s.setNickname);
  const commit = useHeroStore((s) => s.commit);
  const syncCode = useHeroStore((s) => s.syncCode);
  const [savedView, setSavedView] = useState(false);
  /**
   * 이 기기에 정말 남았나. 사생활 보호 모드처럼 저장소를 못 쓰는 기기가 있다 —
   * 그때 「저장 완료!」만 보여 주면 아이는 새로고침 한 번에 캐릭터를 잃는다.
   */
  const [savedLocally, setSavedLocally] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [manifest, setManifest] = useState<AssetManifest | null>(null);
  /** 그림이 올라온 완성 캐릭터만 — 기본 제공 + **운영자가 만든 것**(매니페스트가 목록이다). */
  const assetKeys = manifest ? Object.keys(manifest.assets) : [];
  const presetBases: string[] = pickableBases((k: string) => !!manifest?.assets[k]?.url, assetKeys);
  /**
   * 화면에 쓸 이름 — **운영자가 붙인 이름이 먼저**다.
   * 운영자가 만든 캐릭터는 코드에 이름표가 없다(있을 수도 없다). 그림을 올릴 때 적은 이름이
   * 매니페스트 alt 로 남으므로 그걸 쓴다. 없으면 기본 제공 이름, 그것도 없으면 id.
   */
  const baseLabel = (b: string) =>
    baseLabelOf(b, manifest?.assets[heroBaseKey(b)]?.alt, BASE_LABEL[b]);
  const [step, setStep] = useState<StepId>(1); // 직접 꾸미기 단계(STEPS)
  /**
   * 🔴 **폰에서 단계를 넘기면 칸이 반쯤 잘려 보였다** (2026-09-05, 360×740 실측).
   *
   * 「다음: 모자 →」를 누르면 화면은 그 자리에 그대로 있는다. 그런데 새 단계의 보기가
   * 더 길어서, 위에 있던 단계 칸(① 아이 ② 옷 …)이 **반만 보이고**(「요」·「어요」)
   * 아이 미리보기는 화면 위로 밀려난다. 아이는 자기가 어디쯤인지 모른 채 고른다.
   *
   * `block: "nearest"` 라 **필요한 만큼만** 움직인다 — 이미 다 보이면 아무 일도 안 한다.
   */
  const stepsRef = useRef<HTMLDivElement>(null);
  const firstStep = useRef(true);
  useEffect(() => {
    if (firstStep.current) {
      firstStep.current = false; // 처음 열 때는 맨 위가 맞다 — 건드리지 않는다
      return;
    }
    stepsRef.current?.scrollIntoView({ block: "nearest" });
  }, [step]);
  // "완성 캐릭터" 탭 복귀용 — 마지막으로 골랐던 프리셋 기억(직접 꾸미기 갔다 와도 유지)
  const lastPresetRef = useRef<HeroBase>("explorer_a");
  useEffect(() => {
    if (draft.base !== "custom") lastPresetRef.current = draft.base;
  }, [draft.base]);
  const lastPreset = lastPresetRef.current;

  // 완성형 프리셋 캐릭터 이미지가 게시돼 있으면 캐릭터 썸네일로 보여준다(없으면 텍스트).
  useEffect(() => {
    let alive = true;
    void loadAssetManifest().then((m) => alive && setManifest(m));
    return () => {
      alive = false;
    };
  }, []);
  const hasPresets = !!manifest && presetBases.length > 0;

  // 고를 수 있는 옵션 규칙은 lib/heroOptions 단일 출처(테스트로 지킨다 — 특히 완주 메달 제외).
  const optionsFor = (cat: PickCategory) =>
    pickableOptions(cat, parts.body, parts.outfit, (k) => !!manifest?.assets[k]?.url, assetKeys);
  // 그림이 하나도 안 올라온 단계는 아예 숨긴다(빈 화면 방지).
  const steps = visibleSteps(STEPS, (c) => optionsFor(c).length);
  // "직접 꾸미기"(custom) = 파츠·색상 레이어드 커스터마이즈. 프리셋이 아예 없으면 항상 커스터마이즈 노출(폴백).
  const isCustom = isCustomMode(draft.base, hasPresets ? presetBases.length : 0);

  // ESC 닫기(접근성)
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  function onSave() {
    const cleanNick = sanitizeNickname(draft.nickname);
    if (cleanNick !== draft.nickname) setNickname(cleanNick ?? "");
    commit();
    // 🔴 이 기기에 못 남겼으면 아래 카드에서 말해 준다 — 「저장됐다」만 보여 주면 거짓말이다
    setSavedLocally(heroSavedOk());
    /**
     * 🔴 예전엔 `select_entry` 에 「hero_saved」를 **글자로 실어** 보냈다. 그러면 랜딩의
     * 입장 버튼 통계에 섞이고, 우리 집계(`hero_made`)에는 **아무것도 안 들어간다** —
     * 관리자 [📊 방문자수]의 「내 친구 만들기」가 늘 0 이었다(2026-08-28 발견).
     */
    track({ name: "hero_saved" });
    // 저장 직후 내 히어로 카드(QR/코드) 노출(FR-HERO-10) — 코드 발급은 best-effort
    setSavedView(true);
    setSyncing(true);
    void syncCode().finally(() => setSyncing(false));
  }

  return (
    <div
      className="customizer-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label="나의 히어로 만들기"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="customizer">
        <div className="customizer__head">
          <h2 className="customizer__title">나의 히어로 만들기</h2>
          <button type="button" className="customizer__close" onClick={onClose} aria-label="닫기">
            <UiIcon name="close" size={30} />
          </button>
        </div>

        {savedView ? (
          <div className="customizer__saved">
            <p className="customizer__saved-title">
              {savedLocally ? "히어로 저장 완료! 🎉" : "히어로를 만들었어요! 🎉"}
            </p>
            {/**
             * 🔴 **이 기기에 못 남기는 상황이면 말해 준다.**
             * 그래도 아래 **코드**가 있으면 다른 기기에서 되찾을 수 있다 — 그걸 같이 알려 준다.
             */}
            {!savedLocally ? (
              <p className="customizer__warn">
                ⚠️ 이 기기에는 못 남겨요(사생활 보호 모드일 수 있어요). 아래 <b>코드를 적어 두면</b>{" "}
                언제든 이 친구를 다시 부를 수 있어요.
              </p>
            ) : null}
            <HeroCard hero={draft} syncing={syncing} />
            <div className="customizer__actions">
              <button type="button" className="btn" onClick={onClose}>
                닫기
              </button>
              <button type="button" className="btn btn--primary" onClick={onSaved}>
                플레이 시작 →
              </button>
            </div>
          </div>
        ) : (
          <>
            {/* 최상단 모드 탭 — "완성 캐릭터"와 "직접 꾸미기"를 화면째 분리(캐릭터 줄이 한 화면에 하나만). */}
            {hasPresets ? (
              <div className="customizer__modes" role="tablist" aria-label="꾸미기 방식">
                <button
                  type="button"
                  role="tab"
                  className="customizer__modetab"
                  aria-selected={!isCustom}
                  onClick={() => draft.base === "custom" && setBase(lastPreset)}
                >
                  <UiIcon name="sparkle" size={22} /> 완성 캐릭터
                </button>
                <button
                  type="button"
                  role="tab"
                  className="customizer__modetab"
                  aria-selected={isCustom}
                  onClick={() => setBase("custom")}
                >
                  <UiIcon name="palette" size={22} /> 직접 꾸미기
                </button>
              </div>
            ) : null}

            <div className="customizer__preview">
              <HeroAvatar hero={draft} size={140} idle />
              <button type="button" className="customizer__random" onClick={randomize}>
                <UiIcon name="dice" size={24} /> 랜덤 조합
              </button>
            </div>

            {/* ── 완성 캐릭터 화면 ── */}
            {!isCustom && (
              <>
                <p className="customizer__guide">마음에 드는 친구를 골라요!</p>
                <div className="opt-grid">
                  {presetBases.map((b: string) => (
                    <OptionCard
                      key={b}
                      selected={draft.base === b}
                      label={baseLabel(b)}
                      onClick={() => setBase(b as HeroBase)}
                    >
                      <HeroAvatar hero={{ ...draft, base: b as HeroBase }} size={72} idle={false} />
                    </OptionCard>
                  ))}
                </div>
              </>
            )}

            {/* ── 직접 꾸미기 화면: 단계형(아이 → 옷 → 모자 → 소품) ──
            아이 썸네일은 항상 기본 옷(o0) 고정 — 옷을 바꿔도 아이 선택지의 모습이 안 변함(혼동 방지).
            모자·소품 썸네일은 지금 고른 아이·옷 위에 얹어 보여준다(실제 결과 그대로). */}
            {isCustom &&
              steps.length > 0 &&
              (() => {
                const cur = steps.find((s) => s.id === step) ?? steps[0]!;
                const idx = steps.indexOf(cur);
                const next = steps[idx + 1];
                const prev = steps[idx - 1];
                const opts = optionsFor(cur.cat);
                return (
                  <>
                    <div
                      ref={stepsRef}
                      className="customizer__steps"
                      role="tablist"
                      aria-label="꾸미기 단계"
                    >
                      {steps.map((s) => (
                        <button
                          key={s.id}
                          type="button"
                          role="tab"
                          className="customizer__steptab"
                          aria-selected={s.id === cur.id}
                          onClick={() => setStep(s.id)}
                        >
                          {s.icon} {s.name} — {OPT_LABEL[parts[s.cat]] ?? parts[s.cat]}
                        </button>
                      ))}
                    </div>

                    <p className="customizer__guide">{cur.guide}</p>

                    <div className="opt-grid">
                      {opts.map((opt) => {
                        // 아이를 바꾸면 옷은 기본(o0)으로 되돌린다 — 그 아이에게 없는 옷 조합을 피한다.
                        const previewParts =
                          cur.cat === "body"
                            ? { ...parts, body: opt, outfit: "o0" }
                            : { ...parts, [cur.cat]: opt };
                        const preview = {
                          ...draft,
                          base: "custom" as HeroBase,
                          parts: stringifyParts(previewParts as typeof parts),
                        };
                        return (
                          <OptionCard
                            key={opt}
                            selected={parts[cur.cat] === opt}
                            label={OPT_LABEL[opt] ?? opt}
                            onClick={() => {
                              setPart(cur.cat, opt);
                              if (cur.cat === "body") setPart("outfit", "o0");
                            }}
                          >
                            {/* 완주 메달은 미리보기에서 끈다 — 어떤 옵션을 골랐는지가 가려진다 */}
                            <HeroAvatar hero={preview} size={72} idle={false} medal={false} />
                          </OptionCard>
                        );
                      })}
                    </div>

                    <div className="customizer__stepnav">
                      {prev ? (
                        <button type="button" className="btn" onClick={() => setStep(prev.id)}>
                          ← {prev.name} 다시 고르기
                        </button>
                      ) : null}
                      {next ? (
                        <button
                          type="button"
                          className="btn btn--primary"
                          onClick={() => setStep(next.id)}
                        >
                          다음: {next.name} →
                        </button>
                      ) : null}
                    </div>
                  </>
                );
              })()}

            <div className="customizer__group">
              <label className="customizer__label" htmlFor="nick">
                닉네임 (선택)
              </label>
              <input
                id="nick"
                className="customizer__nick"
                type="text"
                inputMode="text"
                maxLength={16}
                placeholder="비우면 '꼬마손님-####'"
                value={draft.nickname ?? ""}
                onChange={(e) => setNickname(e.target.value)}
              />
              <p className="customizer__hint">
                실명·연락처는 적지 말아주세요. 이 기기에만 저장됩니다(가입 없음).
              </p>
            </div>

            <div className="customizer__actions">
              <button type="button" className="btn" onClick={onClose}>
                <UiIcon name="close" size={20} /> 취소
              </button>
              <button type="button" className="btn btn--primary" onClick={onSave}>
                <UiIcon name="check" size={24} /> 저장하고 플레이
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/** 이름표가 붙은 선택 카드 — 선택 시 금색 테두리 + ✓ 배지(한눈에 보이는 선택 표시). */
function OptionCard({
  selected,
  label,
  onClick,
  children,
}: {
  selected: boolean;
  label: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button type="button" className="opt-card" aria-pressed={selected} onClick={onClick}>
      {selected ? (
        <span className="opt-card__check" aria-hidden="true">
          ✓
        </span>
      ) : null}
      <span className="opt-card__img">{children}</span>
      <span className="opt-card__name">{label}</span>
    </button>
  );
}

/**
 * 🧩 이 창은 첫 화면 **위에** 열린다 — 아이콘은 첫 화면 묶음이 아니라 **열릴 때만 받는 제 묶음**으로
 * (조치계획서 ■2: 첫 화면 용량 0.68MB). UiAtlas 는 겹쳐 쓸 수 있고 안쪽이 이긴다.
 */
export function HeroCustomizer(props: Props) {
  return (
    <UiAtlas bundle="hero">
      <HeroCustomizerInner {...props} />
    </UiAtlas>
  );
}
